import type { ChatSession, Message } from '../types';

/**
 * Carrying the streamed record of a turn across a remote refresh.
 *
 * `stageBuffers` is assembled in the browser out of the SSE stage chunks. The
 * server never builds one — it records the finished turn, not the account of
 * how the turn arrived — so *every* remote snapshot is missing it.
 *
 * While a session is open the client polls the server and adopts the remote
 * copy. Without this merge that adoption silently erases the whole step
 * stream: the user watches the reasoning and its conclusions blink out
 * mid-conversation, typically the moment a clarification card lands, because
 * the poll happens as soon as generation stops. The existing guard on that path
 * only protects turns that produced code, so a turn that stopped to ask a
 * question is exactly the case it does not cover.
 *
 * Local wins only where the remote has nothing: a remote snapshot that does
 * carry stage buffers is newer and is left alone.
 */
export function preserveStreamedTurnRecord(
  remote: ChatSession,
  local: ChatSession | undefined,
): ChatSession {
  if (!local?.messages?.length || !remote.messages?.length) return remote;

  const localById = new Map<string, Message>(
    local.messages.filter(message => message.id).map(message => [message.id, message]),
  );

  let changed = false;
  const messages = remote.messages.map(message => {
    if (message.stageBuffers) return message;
    const kept = localById.get(message.id)?.stageBuffers;
    if (!kept) return message;
    changed = true;
    return { ...message, stageBuffers: kept };
  });

  return changed ? { ...remote, messages } : remote;
}
